/**
 * CatalogProvider: single source of truth for marketplace catalog data.
 *
 * Owns the one fetch of marketplace.json, exposes plugins, loading/error state,
 * and catalog utilities (findByInstallKey, parentIndex, removePlugin) via context.
 * Must be rendered inside AuthGuard so the fetch is gated by authentication.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { MarketplaceEntry, MarketplaceClientConfig } from "../types/index";
import { createMarketplaceClient } from "./marketplace-client";
import { buildParentIndex } from "./parent-plugin";
import { reportError } from "./newrelic";

// ── Install-key helpers ────────────────────────────────────────────────────────
// Moved from App.tsx so the provider and all consumers share one definition.

export function installKeyForPlugin(plugin: MarketplaceEntry): string {
  return (
    plugin.installName ??
    `${plugin.source}--${plugin.orgOrOwner.toLowerCase()}--${plugin.repoName.toLowerCase()}`
  );
}

export function findPluginByInstallKey(
  catalog: MarketplaceEntry[],
  installKey: string,
): MarketplaceEntry | null {
  return (
    catalog.find(
      (p) => installKeyForPlugin(p).toLowerCase() === installKey.toLowerCase(),
    ) ?? null
  );
}

// ── Context ────────────────────────────────────────────────────────────────────

interface CatalogContextValue {
  plugins: MarketplaceEntry[];
  isLoading: boolean;
  error: string | null;
  /** Force a cache-busting re-fetch (e.g. after a transient error). */
  refresh: () => Promise<void>;
  /** Optimistically remove a plugin by install key (used after a successful delete). */
  removePlugin: (installKey: string) => void;
  /** Look up a plugin by its install key. Returns null when not found. */
  findByInstallKey: (installKey: string) => MarketplaceEntry | null;
  /** Inverted skill→parent index built from the catalog. Key = child installName. */
  parentIndex: Map<string, MarketplaceEntry>;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function CatalogProvider({
  children,
  config,
}: {
  children: ReactNode;
  /** Marketplace client config. Pass a stable (memoized) object;
   *  changing it rebuilds the client and re-fetches the catalog. */
  config: Partial<MarketplaceClientConfig>;
}) {
  const [plugins, setPlugins] = useState<MarketplaceEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => createMarketplaceClient(config), [config]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    client
      .getCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setPlugins(catalog.plugins);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        reportError(err, "catalog-fetch-routing");
        setError(err instanceof Error ? err.message : "Failed to load catalog");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const catalog = await client.refreshCatalog();
      setPlugins(catalog.plugins);
    } catch (err: unknown) {
      reportError(err, "catalog-refresh");
      setError(err instanceof Error ? err.message : "Failed to refresh catalog");
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  const removePlugin = useCallback((installKey: string) => {
    setPlugins((prev) =>
      prev.filter((p) => installKeyForPlugin(p).toLowerCase() !== installKey.toLowerCase()),
    );
  }, []);

  const findByInstallKey = useCallback(
    (installKey: string) => findPluginByInstallKey(plugins, installKey),
    [plugins],
  );

  const parentIndex = useMemo(() => buildParentIndex(plugins), [plugins]);

  const value = useMemo<CatalogContextValue>(
    () => ({ plugins, isLoading, error, refresh, removePlugin, findByInstallKey, parentIndex }),
    [plugins, isLoading, error, refresh, removePlugin, findByInstallKey, parentIndex],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useCatalog(): CatalogContextValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }
  return ctx;
}
